import React from "react";
import { RefreshCw } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ServiceSuggestion } from "@/types/services";

interface ServiceSuggestionsProps {
  suggestions: ServiceSuggestion[];
  loadingSuggestions: boolean;
  userProfession: string | null;
  rawProfession: string | null;
  userCurrency: string;
  useSuggestion: (suggestion: ServiceSuggestion) => void;
}

export const ServiceSuggestions = ({
  suggestions,
  loadingSuggestions,
  userProfession,
  rawProfession,
  userCurrency,
  useSuggestion,
}: ServiceSuggestionsProps) => {
  const formatPrice = (price: number) => {
    return `${userCurrency} ${price.toLocaleString()}`;
  };

  return (
    <div className="mb-12">
      <h2 className="text-xl font-semibold mb-4">
        Suggested services for {rawProfession || userProfession || 'your profession'}
      </h2>
      <div className="space-y-6">
        {loadingSuggestions ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative w-16 h-16 mb-4">
              <RefreshCw 
                className="w-16 h-16 text-gebeya-pink gebeya-spinner"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #FF1493, #FFA500)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              />
            </div>
            <p className="text-gray-600">Loading service suggestions...</p>
            <div className="w-full max-w-md mt-4">
              <Progress value={65} className="h-1.5 bg-gray-200" 
                style={{ 
                  '--progress-background': 'linear-gradient(to right, #FF1493, #FFA500)'
                } as React.CSSProperties} 
              />
            </div>
          </div>
        ) : suggestions.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className="p-4 text-left rounded-lg border hover:border-gebeya-pink transition-all cursor-pointer"
                onClick={() => useSuggestion(suggestion)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{suggestion.name}</h3>
                  <span className="text-gebeya-pink font-medium">
                    {formatPrice(suggestion.price)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500">{suggestion.description}</p>
              </div>
            ))}
          </div>
        ) : userProfession ? (
          <div className="text-center py-6">
            <p className="text-gray-500">Loading suggestions for your profession...</p>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No profession information found. You can still add services manually below.</p>
          </div>
        )}
      </div>
    </div>
  );
};
